import React, { Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { useThree } from '@react-three/fiber';
import { AnimatedResident } from './AnimatedResident';
import WorldLabel from './WorldLabel';
import { useWorldStore } from '../state/worldStore';
import { poseFromCamera } from '../services/seats';
import { setSeatSitter } from '../state/seatBridge';
import useRoomSeats from '../hooks/useRoomSeats';
import { ROOMS } from '../rooms';
import { useRemoteBodies } from '../services/bodyRigs';
import { useGLTF } from '@react-three/drei';
import SafeMount from './SafeMount';

// ListeningSeats — the people in the chairs.
//
// Khari, 2026-08-04: "when the participants join (as their avatars), their
// models are placed, sitting on the couch, chair or some other room appropriate
// position to listen to the playlists and other shared selections of the person
// who opened the room."
//
// THE SERVER HANDS OUT AN INDEX, THIS TURNS IT INTO A CHAIR. The server cannot
// see furniture, and letting clients choose a seat is how two people end up in
// one — so it assigns a number and `listSeats()` resolves that number against
// the room's own seat affordances, in an order pinned to the authored name so
// every client agrees which chair is number three.
//
// YOUR OWN SEAT IS NOT DRAWN. You are already in the room, looking out of your
// own eyes — the world's existing `seated` machinery moved your camera there.
// Drawing your body too would put you inside your own head.

// Where a seat pose puts a BODY, as opposed to where it puts a camera.
//
// `seatPose.pos` is an EYE position: the seat surface plus a head-height offset,
// because its first job was moving the player's camera. A body is placed by its
// FEET, so the eye offset has to come back off — otherwise everyone sits a
// head's height above the cushion, which reads as hovering rather than as sitting.
/** The files a seated body loads, for the retry to drop from the GLTF cache. */
function bodyFiles(roster, avatarId) {
  const m = (roster || []).find((n) => n.avatarId === avatarId);
  return m ? [m.rigged, m.idle].filter(Boolean) : [];
}

function bodyAt(pose) {
  if (!pose) return null;
  const [x, y, z] = pose.pos;
  return { pos: [x, y - (pose.reclined ? 0.24 : 0.34), z], yaw: pose.yaw };
}

function SeatedBody({ seat, pose, roster, lightScale }) {
  // ⚠ `pose` here is the SEAT's transform, not the body's rig data — two
  //   different things that would otherwise share a name in one function.
  //   `roster` arrives already carrying the server's bodies (see the parent).
  const model = roster.find((n) => n.avatarId === seat.avatarId);
  const at = bodyAt(pose);
  // The SEAT's size wins over the roster's. roster.json carries one height per
  // avatar, which cannot be right for both an armchair and a low couch — the
  // chair is the thing that knows. Tuned per seat in ⚙ → Placeables.
  const height = pose?.bodyHeight || model?.height || 0.75;
  if (!model?.rigged || !at) {
    // Said out loud, because an empty chair in a room somebody is definitely
    // sitting in is the kind of silence that reads as a broken feature. The seat
    // is still THEIRS on the server — they just have no body we can draw.
    if (!model?.rigged) console.warn(`[listening] ${seat.name || seat.avatarId} has no rigged model in roster.json — seated, but invisible`);
    return null;
  }
  return (
    <group position={at.pos} rotation={[0, at.yaw, 0]}>
      {/* IDLE ONLY, NEVER THE WALK CLIP. A seated body playing a walk cycle is
          the single most obviously wrong thing this could do, and the roster
          entry offers both. */}
      <AnimatedResident
        meshUrl={model.rigged}
        rigPose={model.pose || null}
        clipMap={model.clips || null}
        clipUrls={model.idle ? [model.idle] : []}
        play="idle"
        height={height}
        indoor
        lightScale={lightScale}
        avatarId={seat.avatarId}
        // Desynced per seat so a row of listeners does not breathe in lockstep —
        // the thing that makes a group of NPCs read as a set of clones.
        phaseOffset={(seat.index * 0.37) % 1}
        rate={0.94 + ((seat.index * 0.13) % 0.12)}
      />
      {/* Rides the RESOLVED height — off the roster's it would drift away from
          the head the moment a seat is resized. */}
      <WorldLabel position={[0, height + 0.28, 0]} center distanceFactor={7} style={{ pointerEvents: 'none' }}>
        <div style={{
          background: 'rgba(14,9,6,0.86)', color: '#ffe9c4', font: '600 10.5px system-ui',
          padding: '2px 7px', borderRadius: 999, whiteSpace: 'nowrap',
          border: `1px solid ${seat.host ? '#ffb25e' : 'rgba(255,178,94,0.35)'}`,
        }}>
          {seat.name || 'guest'}{seat.host ? ' ★' : ''}
        </div>
      </WorldLabel>
    </group>
  );
}

export default function ListeningSeats() {
  const room = useWorldStore((s) => s.listeningRoom);
  const interior = useWorldStore((s) => s.interior);
  const myAvatarId = useWorldStore((s) => s.myAvatarId);
  const [rosterRaw, setRoster] = useState([]);

  useEffect(() => {
    fetch('/models/avatars/roster.json', { cache: 'no-store' })
      .then((r) => r.json())
      .then((d) => setRoster((d.npcs || []).filter((n) => n.rigged)))
      .catch(() => setRoster([]));
  }, []);
  /* ⚠ roster.json's GLB paths are a generation behind for most bodies — the
     current mesh lives on the server. Layered on here, once, so every
     SeatedBody below reads an already-current entry. */
  const roster = useRemoteBodies(rosterRaw);

  // The same list the dock counts capacity from — it MUST be the same, since the
  // server hands out a seat INDEX and nothing else.
  const seats = useRoomSeats(interior);

  // ── YOUR OWN CHAIR: FIRST PERSON *OR* THIRD, BY THE ROOM ──────────────────
  //
  // Khari, 2026-08-07: "in the listening room, let me see myself. keep the
  // camera rotation on orbit. This way, people will be able to see their avatars
  // in 3rd person."
  //
  // The room already says which it is. A `firstPerson` room (the salon) puts you
  // behind your own eyes, so your seat gets the CAMERA and drawing your body
  // there would be drawing the inside of your own head. An ORBIT room (this one)
  // leaves you outside looking in — so your seat gets a BODY like everyone
  // else's, and the camera is never taken away from you.
  //
  // Deriving it from `firstPerson` rather than a new flag because that is
  // already exactly the question being asked, and the two answers were the whole
  // bug: seating the camera in an orbit room meant OrbitControls and SeatCamera
  // both aiming it, and orbit wins every frame.
  const thirdPerson = !ROOMS[interior]?.firstPerson;
  const camera = useThree((s) => s.camera);
  const sitAt = useWorldStore((s) => s.sitAt);
  const standUp = useWorldStore((s) => s.standUp);
  const seated = useWorldStore((s) => s.seated);
  const took = useRef(null);

  const mySeat = useMemo(() => (room?.seats || [])
    .find((s) => s && myAvatarId && s.avatarId === myAvatarId) || null, [room, myAvatarId]);

  useEffect(() => {
    // In a third-person room the camera is never claimed, so there is nothing to
    // arm here — and publishing a sitter would give the dock a "Take my seat"
    // button that yanks the view away from someone who can already see their own
    // avatar sitting down.
    if (thirdPerson) { took.current = null; setSeatSitter(null); return undefined; }
    if (!room || !mySeat || room.zone !== interior) { took.current = null; setSeatSitter(null); return undefined; }
    const key = `lr:${room.roomId}:${mySeat.index}`;
    const pose = seats[mySeat.index]?.pose;
    if (!pose) return undefined;   // chairs not resolved yet — re-runs when they are

    const sit = () => sitAt({ target: key, seat: pose, from: poseFromCamera(camera) });
    // Published for the dock's "Take my seat" — the only other way back into a
    // chair you chose to stand up out of. See state/seatBridge.
    setSeatSitter(sit);
    if (took.current !== key) { took.current = key; sit(); }
    return () => setSeatSitter(null);
  }, [thirdPerson, room, mySeat, seats, interior, sitAt, camera]);

  // And back on your feet when you leave — the seat is gone, so holding the
  // camera in it would park you in an empty room with no way to stand.
  useEffect(() => {
    if (mySeat || !seated || seated.phase !== 'seated') return;
    if (typeof seated.target === 'string' && seated.target.startsWith('lr:')) standUp();
  }, [mySeat, seated, standUp]);

  const lightScale = ROOMS[interior]?.light ?? 1;

  const occupied = useMemo(() => (room?.seats || [])
    .filter(Boolean)
    // Not you. See the note up top — you are behind your own eyes already.
    // YOU GET A BODY TOO, unless the room put you behind your own eyes — in
    // which case drawing it would be drawing the inside of your own head.
    .filter((s) => thirdPerson || !(myAvatarId && s.avatarId === myAvatarId))
    .map((s) => ({ seat: s, pose: seats[s.index]?.pose || null }))
    .filter((x) => x.pose), [room, seats, myAvatarId, thirdPerson]);

  if (!room || room.zone !== interior || !occupied.length) return null;

  return (
    <>
      {occupied.map(({ seat, pose }) => (
        // SUSPENSE PER BODY. A resident's GLB suspends while it downloads, and
        // without a boundary of its own that suspension travels up to the room's
        // — which in a splat room tears the whole room down mid-load. Same rule
        // InteriorResidents and Bouncers already follow.
        <SafeMount
          key={`${seat.index}:${seat.avatarId}`}
          label={`seat ${seat.name || seat.avatarId}`}
          retryMs={30000}
          onRetry={() => useGLTF.clear(bodyFiles(roster, seat.avatarId))}
        >
          <Suspense fallback={null}>
            <SeatedBody seat={seat} pose={pose} roster={roster} lightScale={lightScale} />
          </Suspense>
        </SafeMount>
      ))}
    </>
  );
}
